import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { eventoInscricaoAdminService, eventoService, unidadeService } from '../../services/api';
import { handleApiError } from '../../utils/apiError';
import { usePermissaoRecurso } from '../../hooks/usePermissoes';
import AprovacaoComercialModal from './AprovacaoComercialModal';

const { TextArea } = Input;

// Tela geral "Inscrições de Eventos" (Fase B.3b) -- separada de
// EventosAdmin/SessoesPainel (por Evento/Sessão) porque aqui o recorte é
// TRANSVERSAL: todas as inscrições de todos os eventos, com filtros e
// paginação server-side, como pedido pela especificação da fase. NÃO
// duplica nenhuma regra de negócio -- todo botão chama diretamente um
// endpoint que já existe em app/routes/eventos_admin.py sobre o service
// de B.3a/B.4a/B.4b (app/services/evento_inscricao_service.py).
//
// EFETIVADA (Fase B.4b, correção final): a listagem traz
// participacao_status/id_participacao prontos de um LEFT JOIN em lote no
// backend (listar_inscricoes_admin) -- nunca um GET por linha nem um GET
// adicional no clique. Após EFETIVADA, participacao_status
// (CONFIRMADA/CANCELADA) é a autoridade real do estado operacional, não
// `status` (que fica para sempre em EFETIVADA) -- a listagem já distingue
// "Participação confirmada" de "Participação cancelada", e o botão
// "Cancelar participação" só aparece quando ainda está CONFIRMADA.
const STATUS_COLORS = {
  SOLICITADA: 'blue',
  APROVADA: 'green',
  REJEITADA: 'red',
  CANCELADA: 'default',
  EFETIVADA: 'purple',
};

// Fase Eventos-Operacional 2 -- semantica do filtro de status:
//  OPERACIONAL (default): todos EXCETO canceladas (envia ocultar_canceladas=true);
//  TODAS: todos os status, INCLUSIVE canceladas (nao envia status);
//  <STATUS>: somente aquele status (envia status=<STATUS>).
const FILTRO_STATUS_OPERACIONAL = 'OPERACIONAL';
const OPCOES_FILTRO_STATUS = [
  { value: 'OPERACIONAL', label: 'Ativas (sem canceladas)' },
  { value: 'TODAS', label: 'Todas' },
  { value: 'SOLICITADA', label: 'Solicitadas' },
  { value: 'APROVADA', label: 'Aprovadas' },
  { value: 'EFETIVADA', label: 'Efetivadas' },
  { value: 'REJEITADA', label: 'Rejeitadas' },
  { value: 'CANCELADA', label: 'Canceladas' },
];

// Ordem e rotulos dos cards de resumo (sempre os 5 status).
const CARDS_RESUMO = [
  { status: 'SOLICITADA', label: 'Solicitadas' },
  { status: 'APROVADA', label: 'Aprovadas' },
  { status: 'EFETIVADA', label: 'Efetivadas' },
  { status: 'REJEITADA', label: 'Rejeitadas' },
  { status: 'CANCELADA', label: 'Canceladas' },
];

// Traduz o filtro de status (UI) para os parametros do endpoint sem
// sobrecarregar "status ausente" com duas semanticas.
function statusParaParams(filtroStatus) {
  if (filtroStatus === 'TODAS') return {}; // todos, inclusive CANCELADA
  if (!filtroStatus || filtroStatus === FILTRO_STATUS_OPERACIONAL) {
    return { ocultar_canceladas: true }; // visao operacional inicial
  }
  return { status: filtroStatus }; // status especifico
}

const InscricoesEventosAdmin = () => {
  const { loading: permissaoLoading, isAdmin, podeVisualizar, podeEditar } = usePermissaoRecurso('eventos');

  const [itens, setItens] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [resumo, setResumo] = useState(null);

  const [filtroStatus, setFiltroStatus] = useState(FILTRO_STATUS_OPERACIONAL);
  const [filtroUnidade, setFiltroUnidade] = useState(undefined);
  const [filtroNomeAluno, setFiltroNomeAluno] = useState('');
  const [filtroEvento, setFiltroEvento] = useState(undefined);
  const [filtroSessao, setFiltroSessao] = useState(undefined);
  const [eventos, setEventos] = useState([]);
  const [sessoes, setSessoes] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [rejeitarModal, setRejeitarModal] = useState({ open: false, inscricao: null, motivo: '' });
  const [comercialModal, setComercialModal] = useState({ open: false, inscricao: null, modo: 'aprovar' });
  const [efetivarModal, setEfetivarModal] = useState({ open: false, inscricao: null });
  const [cancelarParticipacaoModal, setCancelarParticipacaoModal] = useState({
    open: false,
    inscricao: null,
    participacaoId: null,
    motivo: '',
  });
  const [processandoId, setProcessandoId] = useState(null);
  const [efetivando, setEfetivando] = useState(false);
  const [cancelandoParticipacao, setCancelandoParticipacao] = useState(false);

  const carregar = useCallback(
    async (paginaAtual, tamanhoPagina, filtros, sort = {}) => {
      setLoading(true);
      try {
        const params = {
          page: paginaAtual,
          page_size: tamanhoPagina,
          ...statusParaParams(filtros.status),
        };
        if (filtros.id_evento) params.id_evento = filtros.id_evento;
        if (filtros.id_evento_sessao) params.id_evento_sessao = filtros.id_evento_sessao;
        if (filtros.id_unidades) params.id_unidades = filtros.id_unidades;
        if (filtros.nome_aluno) params.nome_aluno = filtros.nome_aluno;
        if (sort.sortBy && sort.sortOrder) {
          params.sort_by = sort.sortBy;
          params.sort_order = sort.sortOrder;
        }
        const resp = await eventoInscricaoAdminService.listar(params);
        setItens(resp.data.items || []);
        setTotal(resp.data.total || 0);
        setResumo(resp.data.resumo || null);
        setPage(resp.data.page || paginaAtual);
        setPageSize(resp.data.page_size || tamanhoPagina);
      } catch (err) {
        handleApiError(err, 'Erro ao carregar inscrições');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const carregarUnidades = useCallback(async () => {
    try {
      const resp = await unidadeService.list();
      setUnidades(resp.data || []);
    } catch (err) {
      // Silencioso: so limita as opcoes do filtro de Unidade.
    }
  }, []);

  // Seletor de Evento: SEM filtro de status -> inclui eventos cancelados,
  // necessario para consultar inscricoes historicas (Fase 2, Secao 8).
  const carregarEventos = useCallback(async () => {
    try {
      const resp = await eventoService.list({ page: 1, page_size: 200 });
      setEventos(resp.data.items || []);
    } catch (err) {
      // Silencioso: so limita as opcoes do filtro de Evento.
    }
  }, []);

  const carregarSessoesDoEvento = useCallback(async (eventoId) => {
    if (!eventoId) {
      setSessoes([]);
      return;
    }
    try {
      const resp = await eventoService.listSessoes(eventoId, { page: 1, page_size: 200 });
      setSessoes(resp.data || []);
    } catch (err) {
      setSessoes([]);
    }
  }, []);

  useEffect(() => {
    if (!permissaoLoading && (podeVisualizar || isAdmin)) {
      // Abertura: visao operacional (todas exceto canceladas).
      carregar(1, pageSize, { status: FILTRO_STATUS_OPERACIONAL }, {});
      carregarUnidades();
      carregarEventos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissaoLoading, podeVisualizar, isAdmin]);

  const filtrosAtuais = (over = {}) => ({
    status: filtroStatus,
    id_evento: filtroEvento,
    id_evento_sessao: filtroSessao,
    id_unidades: filtroUnidade,
    nome_aluno: filtroNomeAluno,
    ...over,
  });
  const sortAtual = () => ({ sortBy, sortOrder });

  const aplicarFiltros = () => carregar(1, pageSize, filtrosAtuais(), sortAtual());

  const aplicarStatus = (valor) => {
    const proximo = valor || FILTRO_STATUS_OPERACIONAL;
    setFiltroStatus(proximo);
    carregar(1, pageSize, filtrosAtuais({ status: proximo }), sortAtual());
  };

  const aplicarUnidade = (valor) => {
    setFiltroUnidade(valor);
    carregar(1, pageSize, filtrosAtuais({ id_unidades: valor }), sortAtual());
  };

  // Evento -> limpa Sessao anterior, recarrega sessoes do novo evento, pagina 1.
  const aplicarEvento = (valor) => {
    setFiltroEvento(valor);
    setFiltroSessao(undefined);
    carregarSessoesDoEvento(valor);
    carregar(1, pageSize, filtrosAtuais({ id_evento: valor, id_evento_sessao: undefined }), sortAtual());
  };

  const aplicarSessao = (valor) => {
    setFiltroSessao(valor);
    carregar(1, pageSize, filtrosAtuais({ id_evento_sessao: valor }), sortAtual());
  };

  const limparFiltros = () => {
    setFiltroStatus(FILTRO_STATUS_OPERACIONAL);
    setFiltroUnidade(undefined);
    setFiltroNomeAluno('');
    setFiltroEvento(undefined);
    setFiltroSessao(undefined);
    setSessoes([]);
    setSortBy(null);
    setSortOrder(null);
    carregar(1, pageSize, { status: FILTRO_STATUS_OPERACIONAL }, {});
  };

  // onChange unico da Table: paginacao + ordenacao server-side; trocar sort
  // volta a pagina 1; NUNCA ordena localmente.
  const handleTableChange = (paginacao, _filtros, sorter) => {
    const novoSortBy = sorter && sorter.order ? sorter.columnKey || sorter.field : null;
    const novoSortOrder =
      sorter && sorter.order === 'ascend'
        ? 'asc'
        : sorter && sorter.order === 'descend'
        ? 'desc'
        : null;
    const sortMudou = novoSortBy !== sortBy || novoSortOrder !== sortOrder;
    const novaPagina = sortMudou ? 1 : paginacao.current || 1;
    const novoTamanho = paginacao.pageSize || pageSize;
    setSortBy(novoSortBy);
    setSortOrder(novoSortOrder);
    carregar(novaPagina, novoTamanho, filtrosAtuais(), { sortBy: novoSortBy, sortOrder: novoSortOrder });
  };

  const ordemAntd = (key) => (sortBy === key ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null);

  const recarregarPaginaAtual = () => carregar(page, pageSize, filtrosAtuais(), sortAtual());

  const abrirAprovar = (inscricao) => setComercialModal({ open: true, inscricao, modo: 'aprovar' });
  const abrirEditarCondicoes = (inscricao) => setComercialModal({ open: true, inscricao, modo: 'editar' });
  const fecharComercialModal = () => setComercialModal({ open: false, inscricao: null, modo: 'aprovar' });

  const abrirRejeitar = (inscricao) => setRejeitarModal({ open: true, inscricao, motivo: '' });

  const handleRejeitar = async () => {
    const { inscricao, motivo } = rejeitarModal;
    setProcessandoId(inscricao.id);
    try {
      await eventoInscricaoAdminService.rejeitar(inscricao.id, motivo || null);
      message.success('Inscrição rejeitada');
      setRejeitarModal({ open: false, inscricao: null, motivo: '' });
      recarregarPaginaAtual();
    } catch (err) {
      handleApiError(err, 'Erro ao rejeitar inscrição');
    } finally {
      setProcessandoId(null);
    }
  };

  const handleCancelar = async (inscricao) => {
    setProcessandoId(inscricao.id);
    try {
      await eventoInscricaoAdminService.cancelar(inscricao.id);
      message.success('Inscrição cancelada');
      recarregarPaginaAtual();
    } catch (err) {
      handleApiError(err, 'Erro ao cancelar inscrição');
    } finally {
      setProcessandoId(null);
    }
  };

  const abrirEfetivar = (inscricao) => setEfetivarModal({ open: true, inscricao });
  const fecharEfetivar = () => setEfetivarModal({ open: false, inscricao: null });

  const handleEfetivar = async () => {
    const { inscricao } = efetivarModal;
    if (!inscricao) return;
    setEfetivando(true);
    try {
      await eventoInscricaoAdminService.efetivar(inscricao.id);
      message.success('Participação confirmada');
      fecharEfetivar();
      recarregarPaginaAtual();
    } catch (err) {
      handleApiError(err, 'Erro ao efetivar inscrição');
    } finally {
      setEfetivando(false);
    }
  };

  // id_participacao/participacao_status ja vem pronto da listagem (LEFT
  // JOIN em lote no backend, ver listar_inscricoes_admin) -- nenhum GET
  // adicional aqui, nem para exibir o estado nem para agir sobre ele.
  const abrirCancelarParticipacao = (inscricao) => {
    if (!inscricao.id_participacao || inscricao.participacao_status !== 'CONFIRMADA') {
      message.warning('Esta inscrição não possui uma participação confirmada no momento.');
      recarregarPaginaAtual();
      return;
    }
    setCancelarParticipacaoModal({
      open: true,
      inscricao,
      participacaoId: inscricao.id_participacao,
      motivo: '',
    });
  };

  const fecharCancelarParticipacao = () =>
    setCancelarParticipacaoModal({ open: false, inscricao: null, participacaoId: null, motivo: '' });

  const handleCancelarParticipacao = async () => {
    const { participacaoId, motivo } = cancelarParticipacaoModal;
    if (!participacaoId) return;
    setCancelandoParticipacao(true);
    try {
      await eventoInscricaoAdminService.cancelarParticipacao(participacaoId, motivo || null);
      message.success('Participação cancelada');
      fecharCancelarParticipacao();
      recarregarPaginaAtual();
    } catch (err) {
      handleApiError(err, 'Erro ao cancelar participação');
    } finally {
      setCancelandoParticipacao(false);
    }
  };

  const nomeUnidade = (idUnidades) => unidades.find((u) => u.id === idUnidades)?.nome_fantasia || idUnidades;

  const columns = [
    {
      title: 'Aluno',
      key: 'nome_aluno',
      sorter: true,
      sortOrder: ordemAntd('nome_aluno'),
      render: (_, item) => (
        <div>
          <div>{item.nome_aluno}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {item.turma_origem_nome}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Evento / Sessão',
      key: 'evento_nome',
      sorter: true,
      sortOrder: ordemAntd('evento_nome'),
      render: (_, item) => (
        <div>
          <div>{item.evento_nome}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {item.sessao_nome} — {dayjs(item.sessao_data).format('DD/MM/YYYY')}
            {item.status_sessao === 'CANCELADA' && <Tag color="red" style={{ marginLeft: 6 }}>Sessão cancelada</Tag>}
          </Typography.Text>
        </div>
      ),
    },
    { title: 'Unidade', key: 'unidade', render: (_, item) => nomeUnidade(item.id_unidades) },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      sortOrder: ordemAntd('status'),
      render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
    },
    { title: 'Origem', dataIndex: 'origem_solicitacao', key: 'origem_solicitacao' },
    {
      title: 'Solicitado em',
      dataIndex: 'solicitado_em',
      key: 'solicitado_em',
      sorter: true,
      sortOrder: ordemAntd('solicitado_em'),
      render: (v) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Condições',
      key: 'condicoes',
      render: (_, item) =>
        item.status === 'APROVADA' || item.status === 'EFETIVADA' ? (
          <div>
            <div>R$ {Number(item.valor_final).toFixed(2).replace('.', ',')}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {item.quantidade_parcelas > 0 ? `${item.quantidade_parcelas}x` : 'à vista/gratuito'}
            </Typography.Text>
            {item.status === 'EFETIVADA' && (
              <div>
                <Tag color={item.participacao_status === 'CANCELADA' ? 'default' : 'purple'} style={{ marginTop: 4 }}>
                  {item.participacao_status === 'CANCELADA' ? 'Participação cancelada' : 'Participação confirmada'}
                </Tag>
              </div>
            )}
          </div>
        ) : (
          '—'
        ),
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, item) => (
        <Space direction="vertical" size={4}>
          {podeEditar && item.status === 'SOLICITADA' && (
            <Space size={4}>
              <Button size="small" type="primary" loading={processandoId === item.id} onClick={() => abrirAprovar(item)}>
                Aprovar inscrição
              </Button>
              <Button size="small" danger loading={processandoId === item.id} onClick={() => abrirRejeitar(item)}>
                Rejeitar
              </Button>
            </Space>
          )}
          {podeEditar && item.status === 'APROVADA' && (
            <Space size={4}>
              <Button size="small" onClick={() => abrirEditarCondicoes(item)}>
                Editar condições
              </Button>
              <Button size="small" type="primary" onClick={() => abrirEfetivar(item)}>
                Efetivar
              </Button>
            </Space>
          )}
          {podeEditar && (item.status === 'SOLICITADA' || item.status === 'APROVADA') && (
            <Popconfirm
              title="Cancelar esta inscrição?"
              onConfirm={() => handleCancelar(item)}
              okText="Confirmar"
              cancelText="Voltar"
            >
              <Button size="small" loading={processandoId === item.id}>
                Cancelar
              </Button>
            </Popconfirm>
          )}
          {podeEditar && item.status === 'EFETIVADA' && item.participacao_status === 'CONFIRMADA' && (
            <Button size="small" danger onClick={() => abrirCancelarParticipacao(item)}>
              Cancelar participação
            </Button>
          )}
          {item.status === 'REJEITADA' && item.motivo_rejeicao && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Motivo: {item.motivo_rejeicao}
            </Typography.Text>
          )}
        </Space>
      ),
    },
  ];

  if (!permissaoLoading && !podeVisualizar && !isAdmin) {
    return (
      <Card>
        <Typography.Text type="secondary">Você não tem permissão para visualizar Inscrições de Eventos.</Typography.Text>
      </Card>
    );
  }

  return (
    <Card title="Inscrições de Eventos">
      {/* Cards de resumo -- distribuicao por status do UNIVERSO contextual
          (respeita Evento/Sessao/Unidade/Nome; IGNORA o filtro de status).
          Clicar aplica o filtro de status da tabela, sem alterar os cards. */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {CARDS_RESUMO.map(({ status: st, label }) => (
          <Col key={st} flex="1 1 140px">
            <Card
              size="small"
              hoverable
              onClick={() => aplicarStatus(st)}
              style={{ borderColor: filtroStatus === st ? '#1677ff' : undefined }}
            >
              <Statistic title={label} value={(resumo && resumo[st]) || 0} />
            </Card>
          </Col>
        ))}
      </Row>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="Buscar por aluno"
          value={filtroNomeAluno}
          onChange={(e) => setFiltroNomeAluno(e.target.value)}
          onPressEnter={aplicarFiltros}
          style={{ width: 220 }}
          allowClear
        />
        <Select
          placeholder="Evento"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 220 }}
          value={filtroEvento}
          onChange={aplicarEvento}
          options={eventos.map((e) => ({
            value: e.id,
            label: e.status === 'CANCELADO' ? `${e.nome} (cancelado)` : e.nome,
          }))}
        />
        <Select
          placeholder="Sessão"
          allowClear
          disabled={!filtroEvento}
          style={{ width: 240 }}
          value={filtroSessao}
          onChange={aplicarSessao}
          options={sessoes.map((s) => ({
            value: s.id,
            label: `${s.nome} — ${dayjs(s.data).format('DD/MM/YYYY')} ${(s.hora_inicio || '').slice(0, 5)}`.trim(),
          }))}
        />
        <Select
          style={{ width: 190 }}
          value={filtroStatus}
          onChange={aplicarStatus}
          options={OPCOES_FILTRO_STATUS}
        />
        <Select
          placeholder="Unidade"
          allowClear
          style={{ width: 200 }}
          value={filtroUnidade}
          onChange={aplicarUnidade}
          options={unidades.map((u) => ({ value: u.id, label: u.nome_fantasia }))}
        />
        <Button type="primary" onClick={aplicarFiltros}>
          Filtrar
        </Button>
        <Button onClick={limparFiltros}>Limpar</Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading || permissaoLoading}
        dataSource={itens}
        columns={columns}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `Total: ${t}`,
        }}
      />

      <Modal
        title="Rejeitar inscrição"
        open={rejeitarModal.open}
        onCancel={() => setRejeitarModal({ open: false, inscricao: null, motivo: '' })}
        onOk={handleRejeitar}
        confirmLoading={processandoId === rejeitarModal.inscricao?.id}
        okText="Rejeitar"
        okButtonProps={{ danger: true }}
        cancelText="Voltar"
      >
        <Typography.Paragraph type="secondary">
          Motivo da rejeição (opcional, visível para o responsável no Portal):
        </Typography.Paragraph>
        <TextArea
          rows={3}
          maxLength={2000}
          value={rejeitarModal.motivo}
          onChange={(e) => setRejeitarModal((prev) => ({ ...prev, motivo: e.target.value }))}
        />
      </Modal>

      <AprovacaoComercialModal
        open={comercialModal.open}
        inscricao={comercialModal.inscricao}
        modo={comercialModal.modo}
        onClose={fecharComercialModal}
        onSalvo={recarregarPaginaAtual}
      />

      <Modal
        title="Efetivar inscrição"
        open={efetivarModal.open}
        onCancel={efetivando ? undefined : fecharEfetivar}
        onOk={handleEfetivar}
        confirmLoading={efetivando}
        okText="Confirmar efetivação"
        cancelText="Voltar"
      >
        <Typography.Paragraph>
          Confirmar participação de {efetivarModal.inscricao?.nome_aluno} nesta sessão?
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          Após a efetivação, as condições comerciais não poderão mais ser alteradas.
        </Typography.Paragraph>
      </Modal>

      <Modal
        title="Cancelar participação"
        open={cancelarParticipacaoModal.open}
        onCancel={cancelandoParticipacao ? undefined : fecharCancelarParticipacao}
        onOk={handleCancelarParticipacao}
        confirmLoading={cancelandoParticipacao}
        okText="Cancelar participação"
        okButtonProps={{ danger: true }}
        cancelText="Voltar"
      >
        <Typography.Paragraph type="secondary">
          Motivo do cancelamento (opcional):
        </Typography.Paragraph>
        <TextArea
          rows={3}
          maxLength={2000}
          value={cancelarParticipacaoModal.motivo}
          onChange={(e) => setCancelarParticipacaoModal((prev) => ({ ...prev, motivo: e.target.value }))}
        />
      </Modal>
    </Card>
  );
};

export default InscricoesEventosAdmin;
